import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, AtSignIcon, BadgeCheckIcon, BarChart3Icon, BotIcon, BriefcaseIcon, Building2Icon, CalendarDaysIcon, CheckIcon, ChevronDownIcon, FilterIcon, HeadphonesIcon, LinkIcon, ImageIcon, InstagramIcon, LinkedinIcon, MagnetIcon, MapPinIcon, MegaphoneIcon, MessageCircleIcon, PhoneIcon, SendIcon, ShieldCheckIcon, SparklesIcon, TargetIcon, TrendingUpIcon, UsersIcon, VideoIcon, YoutubeIcon, ZapIcon } from 'lucide-react';
import { Form, LandingBlock, LandingBlockType, LandingPage } from '../../types';
import { blockDefinition } from '../../data/blockLibrary';
import { BrandLogo } from '../shared/BrandLogo';
import { Button } from '../ui/Button';
import { VideoReplay } from './VideoReplay';
import { SOCIAL_NETWORKS } from '../../data/socialNetworks';
import { DynamicForm, DynamicFormResult } from './DynamicForm';
const ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  Target: TargetIcon,
  Users: UsersIcon,
  Video: VideoIcon,
  Megaphone: MegaphoneIcon,
  Sparkles: SparklesIcon,
  Bot: BotIcon,
  BarChart3: BarChart3Icon,
  Building2: Building2Icon,
  CalendarDays: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Briefcase: BriefcaseIcon,
  Bot: BotIcon,
  Magnet: MagnetIcon,
  Filter: FilterIcon,
  TrendingUp: TrendingUpIcon,
  ShieldCheck: ShieldCheckIcon,
  Zap: ZapIcon,
  BadgeCheck: BadgeCheckIcon,
  Headphones: HeadphonesIcon
};
interface LandingRendererProps {
  landing: LandingPage;
  form?: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  preview?: boolean;
  /** Muestra los accesos de la plataforma en la barra superior (home de AVA). */
  authLinks?: boolean;
  /** Bloque resaltado en el constructor visual. */
  selectedBlockId?: string | null;
  /** Permite seleccionar un bloque haciendo clic sobre él en la vista previa. */
  onSelectBlock?: (blockId: string) => void;
  /**
   * Dispositivo simulado en la vista previa del constructor. Con 'mobile' se
   * aplican los mismos ajustes de móvil que en la landing pública.
   */
  viewport?: 'desktop' | 'tablet' | 'mobile';
}
const SPACING: Record<LandingBlock['spacing'], string> = {
  compacto: 'py-10 sm:py-14',
  normal: 'py-16 sm:py-24',
  amplio: 'py-24 sm:py-32'
};

/**
 * Destino del CTA principal: el formulario multipaso de cualificación. Si la
 * landing no lo incluye, baja al calendario.
 */
function scrollToForm() {
  const target = document.getElementById('formulario') ?? document.getElementById('calendario');
  target?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  });
}
function cfg<T>(block: LandingBlock, key: string, fallback: T): T {
  const value = block.config[key];
  return (value === undefined || value === null ? fallback : value) as T;
}

/** Interruptores del constructor: guardan «si» / «no» o un booleano. */
function flag(block: LandingBlock, key: string, fallback: boolean): boolean {
  const value = block.config[key];
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  return String(value) !== 'no';
}
function blockLabel(type: LandingBlockType): string {
  return blockDefinition(type).label;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 4px 20px -6px rgba(11,31,77,0.10)',
  media: '0 14px 36px -12px rgba(11,31,77,0.20)',
  fuerte: '0 28px 64px -18px rgba(11,31,77,0.30)'
};
const BACKGROUNDS: Record<string, string | undefined> = {
  transparente: undefined,
  blanco: '#FFFFFF',
  gris: '#F5F7FA',
  marca: 'var(--primary)',
  oscuro: '#0B1F4D'
};

/**
 * Ajustes de móvil del bloque: se publican como variables CSS y una clase por
 * propiedad definida, para que `index.css` las aplique solo bajo 640 px. Así la
 * versión móvil se edita sin alterar escritorio ni tablet, y el constructor y
 * la landing pública comparten exactamente las mismas reglas.
 */
function deviceStyle(block: LandingBlock, device: 'm' | 't'): {
  vars: Record<string, string>;
  className: string;
} {
  const s = device === 'm' ? block.style?.mobile : block.style?.tablet;
  if (!s || s.sync !== false) return {
    vars: {},
    className: ''
  };
  const vars: Record<string, string> = {};
  const classes: string[] = [];
  const add = (key: string, value: string | undefined) => {
    if (value === undefined) return;
    vars[`--${device}-${key}`] = value;
    classes.push(`ava-${device}-${key}`);
  };
  add('title', s.titleSize !== undefined ? `${s.titleSize}px` : undefined);
  add('body', s.bodySize !== undefined ? `${s.bodySize}px` : undefined);
  add('lh', s.lineHeight !== undefined ? String(s.lineHeight) : undefined);
  add('ls', s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined);
  add('weight', s.titleWeight !== undefined ? String(s.titleWeight) : undefined);
  add('align', s.align);
  add('py', s.paddingY !== undefined ? `${s.paddingY}px` : undefined);
  add('px', s.paddingX !== undefined ? `${s.paddingX}px` : undefined);
  return {
    vars,
    className: classes.join(' ')
  };
}

/** Ajustes responsive (móvil + tablet) combinados de un bloque. */
function mobileStyle(block: LandingBlock): {
  vars: React.CSSProperties;
  className: string;
} {
  const mobile = deviceStyle(block, 'm');
  const tablet = deviceStyle(block, 't');
  return {
    vars: {
      ...mobile.vars,
      ...tablet.vars
    } as React.CSSProperties,
    className: `${mobile.className} ${tablet.className}`.trim()
  };
}

/** Traduce el estilo editable del bloque a CSS en línea. */
function wrapperStyle(block: LandingBlock): React.CSSProperties {
  const s = block.style;
  if (!s) return {};
  return {
    backgroundColor: s.background ? BACKGROUNDS[s.background] : undefined,
    color: s.textColor,
    paddingTop: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingBottom: s.paddingY !== undefined ? `${s.paddingY}px` : undefined,
    paddingLeft: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    paddingRight: s.paddingX !== undefined ? `${s.paddingX}px` : undefined,
    borderRadius: s.radius !== undefined ? `${s.radius}px` : undefined,
    boxShadow: s.shadow ? SHADOWS[s.shadow] : undefined,
    textAlign: s.align,
    letterSpacing: s.letterSpacing !== undefined ? `${s.letterSpacing}px` : undefined,
    fontWeight: s.titleWeight,
    textTransform: s.uppercase ? 'uppercase' : undefined,
    overflow: s.radius ? 'hidden' : undefined
  };
}
const BUTTON_ANIMATION: Record<string, string> = {
  ninguna: '',
  elevar: 'hover:-translate-y-0.5',
  escalar: 'hover:scale-[1.03]',
  pulso: 'animate-pulse'
};
const BUTTON_ICONS: Record<string, React.ComponentType<{
  className?: string;
}>> = {
  ArrowRight: ArrowRightIcon,
  Sparkles: SparklesIcon,
  Send: SendIcon,
  Calendar: CalendarDaysIcon,
  MessageCircle: MessageCircleIcon,
  Phone: PhoneIcon
};

/** Botón de un bloque, totalmente personalizable desde el constructor. */
function BlockCta({
  block,
  label,
  onClick
}: {
  block: LandingBlock;
  label: string;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  const b = block.button ?? {};
  const Icon = b.icon ? BUTTON_ICONS[b.icon] : undefined;
  const style: React.CSSProperties = {
    backgroundColor: hover && b.hoverColor ? b.hoverColor : b.color,
    color: b.textColor,
    borderRadius: b.radius !== undefined ? `${b.radius}px` : undefined,
    height: b.height !== undefined ? `${b.height}px` : undefined,
    paddingLeft: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    paddingRight: b.paddingX !== undefined ? `${b.paddingX}px` : undefined,
    width: b.fullWidth ? '100%' : undefined
  };
  return <Button size="lg" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={style} className={`h-14 w-full rounded-xl px-8 transition-transform duration-200 sm:w-auto ${b.shadow === false ? '' : 'ava-glow'} ${BUTTON_ANIMATION[b.animation ?? 'elevar'] ?? ''}`} rightIcon={Icon ? <Icon className="h-4 w-4" data-id="element-452" data-mp="1n2j3xa7wephd" /> : undefined} data-id="element-451" data-mp="2uny4jc5b4ne3">
      {b.label || label}
    </Button>;
}

/** Resalta en azul uno o varios fragmentos, separados por "|". */
function Highlighted({
  text,
  highlight
}: {
  text: string;
  highlight: string;
}) {
  const segments = highlight.split('|').map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return <>{text}</>;
  const escaped = segments.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const parts = text.split(new RegExp(`(${escaped.join('|')})`, 'g'));
  return <>
      {parts.map((part, index) => segments.includes(part) ? <span key={`${part}-${index}`} className="text-primary" data-id="element-453" data-mp="1wzx6c5wzldqo">
            {part}
          </span> : <React.Fragment key={`t-${index}`} data-id="element-454" data-mp="1s12nj3z0rp6k">{part}</React.Fragment>)}
    </>;
}

/** Anotación manuscrita con flecha apuntando al vídeo. */
function HandwrittenNote({
  text
}: {
  text: string;
}) {
  return <div className="mt-6 flex items-start justify-center gap-2 sm:gap-3" data-id="element-455" data-mp="glxdfewk6ocx">
      <svg width="48" height="34" viewBox="0 0 48 34" fill="none" aria-hidden="true" className="mt-1 flex-none text-primary" data-id="element-456" data-mp="go0c0nbs1pt9">
        <path d="M46 30C33 33 12 31 4 20C1.5 16.4 2.5 10 8 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" data-id="element-457" data-mp="2x83ys4hj3d9j" />
        <path d="M4 2.5L8.2 6.2L3.4 9.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-458" data-mp="318w97124zo7e" />
      </svg>
      <p className="font-hand text-xl text-primary sm:text-2xl" data-id="element-459" data-mp="362s3ntj6ysxt">{text}</p>
    </div>;
}
export function LandingRenderer({
  landing,
  form,
  onSubmit,
  preview = false,
  authLinks = false,
  selectedBlockId = null,
  onSelectBlock,
  viewport = 'desktop'
}: LandingRendererProps) {
  const config = landing.pageConfig;
  const theme = config.theme;
  const surfaceClass = theme === 'claro_corporativo' ? 'bg-surface' : 'bg-background';
  const cardShadow = theme === 'minimalista' ? '' : 'shadow-soft';
  const blocks = config.blocks.filter(b => b.visible);
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Al seleccionar un bloque en el constructor, la vista previa se desplaza
  // suavemente hasta él para mantener panel y preview sincronizados.
  React.useEffect(() => {
    if (!selectedBlockId || !onSelectBlock) return;
    const target = rootRef.current?.querySelector(`[data-block-id="${selectedBlockId}"]`);
    target?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [selectedBlockId, onSelectBlock]);

  /** Logotipo de la empresa, con altura configurable por bloque. */
  function mark(height = 30) {
    return <BrandLogo src={config.logoUrl} size={height} alt={landing.name} data-id="element-460" data-mp="34x6mzzon7fwf" />;
  }
  const brandMark = mark();
  function renderBlock(block: LandingBlock) {
    switch (block.type) {
      case 'topbar':
        {
          const ctaLabel = cfg(block, 'ctaLabel', '');
          const centered = cfg(block, 'logoAlign', 'center') === 'center';
          const showLogo = flag(block, 'showLogo', true);
          const logoHeight = Number(cfg(block, 'logoHeight', 34)) || 34;
          // El acceso «Iniciar sesión» se puede ocultar desde el editor sin
          // dejar espacio vacío: si no hay nada que mostrar, no se renderiza.
          const showAuth = authLinks && flag(block, 'showAuth', true);
          const actions = <div className="flex items-center gap-2 sm:gap-4" data-id="element-461" data-mp="1317t2fbnrkcn">
            {showAuth && <Link to="/login" className="whitespace-nowrap text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground sm:text-sm" data-id="element-462" data-mp="y2ebobt93977">
                Iniciar sesión
              </Link>}
            {ctaLabel && <Button size="sm" onClick={scrollToForm} className="whitespace-nowrap rounded-full px-4 text-[13px] sm:px-5 sm:text-sm" data-id="element-463" data-mp="1qqsf5733yxq5">
                {ctaLabel}
              </Button>}
          </div>;
          const hasActions = Boolean(ctaLabel) || showAuth;
          return <header key={block.id} className="sticky top-0 z-30 w-full border-b border-border/70 bg-background/80 backdrop-blur-xl" data-id="element-464" data-mp="2eg4ktwz88nnp">
            {centered ?
            // Logo centrado: las acciones se reservan su propio espacio a
            // izquierda y derecha, así nunca se solapan con el logotipo.
            <div className="mx-auto grid max-w-6xl grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 py-3 sm:gap-3 sm:px-8 sm:py-4" data-id="element-465" data-mp="cqyq17kppnnz">
                <span aria-hidden="true" data-id="element-466" data-mp="3u6x1uy6yq72u" />
                <div className="flex min-w-0 justify-center" data-id="element-467" data-mp="30m87n4a88lj">{showLogo && mark(logoHeight)}</div>
                <div className="flex justify-end" data-id="element-468" data-mp="3l6qpxgqwxf5s">{hasActions && actions}</div>
              </div> : <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-8 sm:py-4" data-id="element-469" data-mp="3p83y3xhh9ufv">
                {showLogo ? mark(logoHeight) : <span data-id="element-470" data-mp="3b9vuj6pxruvq" />}
                {hasActions && actions}
              </div>}
          </header>;
        }
      case 'logo':
        return <div key={block.id} className="flex justify-center px-5 pt-14" data-id="element-471" data-mp="3g98qqi2p6eaz">
            {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
          </div>;
      case 'hero':
        {
          const title = cfg(block, 'title', '');
          const eyebrow = cfg(block, 'eyebrow', '');
          return <section key={block.id} className="px-4 pb-4 pt-9 text-center sm:px-8 sm:pb-6 sm:pt-20" data-id="element-472" data-mp="2yvqscb4dd4si">
            <div className="ava-reveal mx-auto w-full max-w-4xl" data-id="element-473" data-mp="2tx5qk79gyd1y">
              {flag(block, 'showLogo', false) && <div className="mb-8 flex justify-center sm:mb-12" data-id="element-474" data-mp="38ppsx5osogic">
                  {mark(Number(cfg(block, 'logoHeight', 34)) || 34)}
                </div>}
              {eyebrow && <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-primary sm:text-xs sm:tracking-[0.3em]" data-id="element-475" data-mp="3hn03spxpa3mq">
                  {eyebrow}
                </p>}
              {cfg(block, 'brandLine', '') && <p className="mx-auto mt-2.5 max-w-[19rem] text-[10px] font-bold uppercase leading-[1.5] tracking-[0.14em] text-navy sm:mt-4 sm:max-w-none sm:text-[13px] sm:tracking-[0.3em]" data-id="element-476" data-mp="3rlzcu9mdo1a9">
                  {cfg(block, 'brandLine', '')}
                </p>}
              {/*
                Móvil: tamaño fluido (30–38 px según el ancho real del
                dispositivo) e interlineado 1.12, para que el titular ocupe
                menos líneas y no se parta de forma poco elegante en 375–430 px.
                Los saltos de línea manuales de la plantilla se respetan solo
                desde tablet: en móvil el texto fluye y decide sus propios
                cortes. Escritorio queda exactamente igual que antes.
               */}
              <h1 className="mx-auto mt-4 max-w-3xl whitespace-normal text-[clamp(30px,7.6vw,38px)] font-extrabold leading-[1.12] tracking-[-0.02em] text-navy sm:mt-9 sm:whitespace-pre-line sm:text-[54px] sm:font-bold sm:leading-[1.08] sm:tracking-[-0.025em]" data-id="element-477" data-mp="mqh1nvlun2c">
                <Highlighted text={title} highlight={cfg(block, 'highlight', '')} data-id="element-478" data-mp="9ksfbvnyf1lx" />
              </h1>
              {/* Subtítulo ancho (88–92 % en móvil, dos líneas en escritorio),
                  gris elegante y con «15 días» resaltado. */}
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 w-[92%] max-w-[38rem] text-[16px] leading-[1.55] text-muted-foreground sm:mt-7 sm:w-full sm:max-w-2xl sm:text-[17px] sm:leading-relaxed" data-id="element-479" data-mp="3i15clnw78vff">
                  <Highlighted text={cfg(block, 'subtitle', '')} highlight={cfg(block, 'subtitleHighlight', '')} data-id="element-480" data-mp="35l5qnhb3kj9p" />
                </p>}
              {cfg(block, 'extraLine', '') && <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground" data-id="element-481" data-mp="2wjobc15iklm2">
                  {cfg(block, 'extraLine', '')}
                </p>}
            </div>
          </section>;
        }
      case 'titulo':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-482" data-mp="2mtcsz9l329vn">
            <h2 className="mx-auto max-w-3xl text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-483" data-mp="2hu8k6k22me5u">
              {cfg(block, 'text', '')}
            </h2>
          </section>;
      case 'subtitulo':
        return <section key={block.id} className="px-5 pb-8" data-id="element-484" data-mp="28ssakej2yg2o">
            <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground" data-id="element-485" data-mp="23uqsnwxe4mss">
              {cfg(block, 'text', '')}
            </p>
          </section>;
      case 'imagen':
        return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-486" data-mp="1u9hihzccn2al">
            {cfg(block, 'url', '') ? <img src={cfg(block, 'url', '')} alt={cfg(block, 'alt', '')} loading="lazy" decoding="async" className={`mx-auto w-full max-w-5xl rounded-2xl object-cover ${cardShadow}`} data-id="element-487" data-mp="1z3v8fm88sh7v" /> : <div className="mx-auto flex aspect-video w-full max-w-5xl items-center justify-center rounded-2xl border border-dashed border-border bg-surface text-sm text-muted-foreground" data-id="element-488" data-mp="1p8a8dtmqhr0r">
                Añade una imagen desde el editor
              </div>}
          </section>;
      case 'video':
        {
          const num = (key: string): number | undefined => {
            const raw = String(block.config[key] ?? '').trim();
            if (raw === '') return undefined;
            const parsed = Number.parseInt(raw, 10);
            return Number.isFinite(parsed) ? parsed : undefined;
          };
          const frame = {
            radius: num('radius'),
            radiusTablet: num('radiusTablet'),
            radiusMobile: num('radiusMobile'),
            radiusCustom: cfg(block, 'radiusCustom', ''),
            borderWidth: num('borderWidth'),
            borderColor: cfg(block, 'borderColor', ''),
            shadow: (cfg(block, 'shadowStyle', '') || undefined) as 'ninguna' | 'suave' | 'media' | 'fuerte' | undefined,
            maxWidth: num('maxWidth')
          };
          const gap = num('gap');
          return <section key={block.id} className="ava-reveal ava-reveal-1 px-4 pb-4 pt-7 sm:px-8 sm:pb-8 sm:pt-10" style={gap === undefined ? undefined : {
            paddingTop: gap,
            paddingBottom: gap
          }} data-id="element-489" data-mp="1aiyjglot2aq">
            <VideoReplay url={cfg(block, 'url', '')} thumbnailUrl={cfg(block, 'thumbnailUrl', '')} overlayTitle={cfg(block, 'overlayTitle', '') ? <Highlighted text={cfg(block, 'overlayTitle', '')} highlight={cfg(block, 'overlayHighlight', '')} data-id="element-491" data-mp="1qne33z7xhtz6" /> : undefined} maxWidthClass="max-w-[760px]" editing={preview} frame={frame} data-id="element-490" data-mp="b7dtxqmnymks" />
            {cfg(block, 'note', '') && <HandwrittenNote text={cfg(block, 'note', '')} data-id="element-492" data-mp="3g4hzrk8pnb11" />}
            {cfg(block, 'caption', '') && <p className="mx-auto mt-6 max-w-2xl text-center text-base text-muted-foreground" data-id="element-493" data-mp="3l3bf7i7k5c3u">
                {cfg(block, 'caption', '')}
              </p>}
            {cfg(block, 'ctaLabel', '') && <div className="mx-auto mt-9 flex max-w-[380px] justify-center" data-id="element-494" data-mp="3uwjukpvxbdu7">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', '')} onClick={scrollToForm} data-id="element-495" data-mp="2qn24q0ikab5" />
              </div>}
          </section>;
        }
      case 'beneficios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-496" data-mp="hmcnqn1selfc">
            <div className="mx-auto max-w-6xl" data-id="element-497" data-mp="2jg27vhi4opkr">
              {cfg(block, 'title', '') && <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-498" data-mp="2jgbwb0niyooc">
                  {cfg(block, 'title', '')}
                </h2>}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-499" data-mp="vtfpl0p9at8f">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`group rounded-2xl border border-border bg-background p-8 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-500" data-mp="mulz7s2iqz3b" data-mpi={index}>
                      <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 transition-colors group-hover:bg-primary/15" data-id="element-501" data-mp="1fg3vs9ijwolo">
                        <Icon className="h-6 w-6 text-primary" data-id="element-502" data-mp="15j8yfty0ku8z" />
                      </span>
                      <h3 className="mb-3 text-base font-semibold text-navy" data-id="element-503" data-mp="3vadwdthc4chf">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-504" data-mp="3qccdp9ntmf6h">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'pilares':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          /*
            Panel blanco con sombra suave, iconos azules sobre círculo claro y
            divisores verticales solo en escritorio. En móvil se apila con
            padding reducido, sin scroll horizontal.
          */
          return <section key={block.id} className="px-4 py-8 sm:px-8 sm:py-12" data-id="element-505" data-mp="er3zsxbppnfm">
            <div className="mx-auto max-w-5xl rounded-3xl border border-border/70 bg-background px-5 py-9 shadow-soft sm:px-8 sm:py-12" data-id="element-506" data-mp="9skbiq9nbzk3">
              <div className="grid gap-9 sm:grid-cols-3 sm:gap-0" data-id="element-507" data-mp="2zgtcb3oy0x55">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? SparklesIcon;
                  return <article key={`${item.title}-${index}`} className={`px-1 text-center sm:px-7 ${index > 0 ? 'sm:border-l sm:border-border/70' : ''}`} data-id="element-508" data-mp="1a3d78ca1psfd" data-mpi={index}>
                      <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10" data-id="element-509" data-mp="1jzg9di4wdr2b">
                        <Icon className="h-7 w-7 text-primary" aria-hidden="true" data-id="element-510" data-mp="1orghhxv4fq9z" />
                      </span>
                      <h3 className="mb-3 text-lg font-bold text-navy" data-id="element-511" data-mp="1tp86aj0gyw9y">
                        {item.title}
                      </h3>
                      <p className="mx-auto max-w-xs text-[13px] leading-relaxed text-muted-foreground sm:text-sm" data-id="element-512" data-mp="22qg655imw565">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'confianza':
        {
          // Franja de confianza: cuatro puntos breves, en fila en escritorio y
          // en 2 × 2 en móvil. Todo el contenido es editable y ocultable.
          const items = cfg<{
            icon: string;
            text: string;
          }[]>(block, 'items', []);
          if (items.length === 0) return null;
          return <section key={block.id} className="px-4 pb-10 pt-2 sm:px-8 sm:pb-14" data-id="element-513" data-mp="f9sxjz84lhdh">
            <ul className="mx-auto grid max-w-5xl grid-cols-2 gap-4 rounded-2xl border border-border/70 bg-background px-5 py-6 shadow-soft sm:grid-cols-4 sm:gap-6 sm:px-8" data-id="element-514" data-mp="a9nakwcswj1q">
              {items.map((item, index) => {
                const Icon = ICONS[item.icon] ?? ShieldCheckIcon;
                return <li key={`${item.text}-${index}`} className="flex items-center justify-center gap-2.5 text-center" data-id="element-515" data-mp="xzr3a8ogaydy" data-mpi={index}>
                    <Icon className="h-4 w-4 flex-none text-primary" aria-hidden="true" data-id="element-516" data-mp="t1pkzpyun33j" />
                    <span className="text-[12px] font-medium leading-snug text-muted-foreground sm:text-[13px]" data-id="element-517" data-mp="17yqe9xwov644">
                      {item.text}
                    </span>
                  </li>;
              })}
            </ul>
          </section>;
        }
      case 'frase':
        return <section key={block.id} className="px-5 py-12 sm:px-8 sm:py-16" data-id="element-518" data-mp="1qmhc8ed19rbf">
            <div className="mx-auto flex max-w-4xl items-center gap-5 sm:gap-8" data-id="element-519" data-mp="1cm6m5djjsgrb">
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-520" data-mp="1coalrjaeytls" />
              <p className="flex-1 whitespace-pre-line text-center text-[11px] font-bold uppercase leading-relaxed tracking-[0.16em] text-navy sm:flex-none sm:text-sm sm:tracking-[0.18em]" data-id="element-521" data-mp="1hjg9mnndmici">
                {cfg(block, 'text', '')}
              </p>
              <span className="hidden h-px flex-1 bg-border sm:block" aria-hidden="true" data-id="element-522" data-mp="tw8cit03qflk" />
            </div>
          </section>;
      case 'estadisticas':
        {
          const items = cfg<{
            value: string;
            label: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]}`} data-id="element-523" data-mp="owc7cqfo4i4f">
            <div className="mx-auto grid max-w-4xl gap-10 text-center sm:grid-cols-3" data-id="element-524" data-mp="1s7jdorldvhcq">
              {items.map(item => <div key={item.label} data-id="element-525" data-mp="1n8gazdsvln7x">
                  <p className="text-4xl font-semibold tracking-tight text-primary sm:text-5xl" data-id="element-526" data-mp="1n9rav2j7aatr">
                    {item.value}
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground" data-id="element-527" data-mp="1dimxg796ay11">{item.label}</p>
                </div>)}
            </div>
          </section>;
        }
      case 'testimonios':
        {
          const items = cfg<{
            name: string;
            role: string;
            company: string;
            text: string;
            avatar?: string;
            result?: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-528" data-mp="ukkdi6vhfra3">
            <div className="mx-auto max-w-6xl" data-id="element-529" data-mp="2tx4zgvhd3w44">
              <h2 className="mb-14 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-530" data-mp="2tush4h7ozjqj">
                {cfg(block, 'title', 'Testimonios')}
              </h2>
              <div className="grid gap-6 md:grid-cols-3" data-id="element-531" data-mp="2wgrs45atzwfm">
                {items.map(item => <figure key={item.name} className={`flex flex-col rounded-2xl border border-border bg-background p-8 ${cardShadow}`} data-id="element-532" data-mp="31fcm3mzhpjvm">
                    {item.result && <span className="mb-5 inline-flex w-fit items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-533" data-mp="3b67j9me9y3vu">
                        {item.result}
                      </span>}
                    <blockquote className="flex-1 text-[15px] leading-relaxed text-foreground" data-id="element-534" data-mp="2ih9zfeuezz0z">
                      “{item.text}”
                    </blockquote>
                    <figcaption className="mt-7 flex items-center gap-3 border-t border-border pt-6" data-id="element-535" data-mp="2nipmtdk2ehmj">
                      {item.avatar ? <img src={item.avatar} alt="" loading="lazy" decoding="async" className="h-11 w-11 rounded-full object-cover" data-id="element-536" data-mp="2qe3j9rrowfu" /> : <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface text-sm font-semibold text-muted-foreground" data-id="element-537" data-mp="7p8ke666caxb">
                          {item.name.charAt(0)}
                        </span>}
                      <span className="min-w-0 flex-1 text-sm" data-id="element-538" data-mp="1ysqw95pzgyvd">
                        <span className="block truncate font-semibold text-foreground" data-id="element-539" data-mp="2ct1ni9v7gfh2">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground" data-id="element-540" data-mp="28shqd6amtbux">
                          {item.role}
                        </span>
                      </span>
                      <span className="hidden text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block" data-id="element-541" data-mp="1g4lxyds3i63y">
                        {item.company}
                      </span>
                    </figcaption>
                  </figure>)}
              </div>
            </div>
          </section>;
        }
      case 'logos_clientes':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className="px-5 py-14" data-id="element-542" data-mp="xaqgi3riuztm">
            <p className="mb-8 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground" data-id="element-543" data-mp="n7uka4ytjh8w">
              {cfg(block, 'title', '')}
            </p>
            <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-12 gap-y-6" data-id="element-544" data-mp="dayj6kb91xed">
              {items.map(name => <span key={name} className="text-lg font-semibold tracking-tight text-muted-foreground/70" data-id="element-545" data-mp="iab4h0mh2weu">
                  {name}
                </span>)}
            </div>
          </section>;
        }
      case 'formulario':
        return <section key={block.id} id="formulario" className={`scroll-mt-24 px-4 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-546" data-mp="1avun439emy3u">
            {/* Sin título ni subtítulo la tarjeta del formulario arranca sola,
                como en la plantilla oficial. */}
            {(cfg(block, 'title', '') || cfg(block, 'subtitle', '')) && <div className="mx-auto mb-10 max-w-2xl text-center sm:mb-12" data-id="element-547" data-mp="3lq8jbmvkwsji">
                {cfg(block, 'title', '') && <h2 className="text-[26px] font-semibold leading-tight tracking-tight text-navy sm:text-4xl" data-id="element-548" data-mp="32zphlxqz0j0p">
                    {cfg(block, 'title', '')}
                  </h2>}
                {cfg(block, 'subtitle', '') && <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground sm:text-lg" data-id="element-549" data-mp="2t100w8at94m1">
                    {cfg(block, 'subtitle', '')}
                  </p>}
              </div>}
            {form ? <DynamicForm form={form} onSubmit={onSubmit} preview={preview} showProgress={cfg(block, 'showProgress', 'si') !== 'no'} hidePrivacyNotice={cfg(block, 'showPrivacy', 'si') === 'no'} data-id="element-550" data-mp="3lpxisab40ze9" /> : <div className="mx-auto max-w-2xl rounded-2xl border border-dashed border-border bg-surface p-12 text-center text-sm text-muted-foreground" data-id="element-551" data-mp="3c0dzx4ned5je">
                Asocia un formulario a esta landing desde el editor.
              </div>}
          </section>;
      case 'faq':
        {
          const items = cfg<{
            q: string;
            a: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-552" data-mp="1m7ls82955znp">
            <div className="mx-auto max-w-3xl" data-id="element-553" data-mp="1r6f79z42typz">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-554" data-mp="1r785gboyzfwi">
                {cfg(block, 'title', 'Preguntas frecuentes')}
              </h2>
              <div className="space-y-3" data-id="element-555" data-mp="20868aqno9hdi">
                {items.map(item => <details key={item.q} className="group rounded-xl border border-border bg-background px-5 py-4 transition-colors hover:border-primary/30" data-id="element-556" data-mp="2567vnns4s2us">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-medium text-foreground marker:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-id="element-557" data-mp="2a3q4le65ei2s">
                      {item.q}
                      <ChevronDownIcon className="h-4 w-4 flex-none text-muted-foreground transition-transform duration-200 group-open:rotate-180" aria-hidden="true" data-id="element-558" data-mp="2nxh2fpaiodio" />
                    </summary>
                    <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-id="element-559" data-mp="3t6ex9q4rkczz">
                      {item.a}
                    </p>
                  </details>)}
              </div>
            </div>
          </section>;
        }
      case 'calendario':
        {
          // La reserva usa el calendario global de la empresa salvo que la
          // landing tenga uno propio. La lógica de reserva no cambia.
          const custom = cfg(block, 'calendarSource', 'global') === 'custom';
          const bookingUrl = String((custom ? cfg(block, 'url', '') : cfg(block, 'globalUrl', '') || cfg(block, 'url', '')) as string).trim();
          // Sin enlace válido el bloque no se muestra en la landing pública:
          // así nunca queda un hueco ni se carga ningún script de calendario.
          if (!bookingUrl && !preview) return null;
          const embedded = cfg(block, 'calendarMode', 'boton') === 'incrustado' && Boolean(bookingUrl);
          const embedHeight = Number(cfg(block, 'embedHeight', 700)) || 700;
          if (embedded) {
            return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-560" data-mp="1drjl8d7gs2er">
              <div className="mx-auto w-full max-w-3xl text-center" data-id="element-561" data-mp="1njfvemmtrizc">
                <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-562" data-mp="1nlto4ou18j3q">
                  {cfg(block, 'title', 'Agenda una llamada')}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-563" data-mp="uvtl6vj3dg3i">
                  {cfg(block, 'text', '')}
                </p>
                <div className="mt-8 overflow-hidden rounded-3xl border border-border bg-background shadow-soft" data-id="element-564" data-mp="14thowayodxxt">
                  <iframe src={bookingUrl} title={cfg(block, 'title', 'Calendario de reservas')} className="w-full" style={{
                    height: `${embedHeight}px`
                  }} loading="lazy" data-id="element-565" data-mp="ztd78hm16x60" />
                </div>
              </div>
            </section>;
          }
          return <section key={block.id} id="calendario" className={`scroll-mt-24 px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-566" data-mp="2j8y3c5dm3cer">
            <div className="mx-auto w-full max-w-3xl rounded-3xl border border-border bg-background p-7 text-center shadow-soft sm:p-12" data-id="element-567" data-mp="2na04irr39yjd">
              <CalendarDaysIcon className="mx-auto mb-5 h-8 w-8 text-primary" aria-hidden="true" data-id="element-568" data-mp="2x9r06wrdyjl1" />
              <h2 className="text-2xl font-bold tracking-tight text-navy sm:text-3xl" data-id="element-569" data-mp="1zm0fbw9feg6w">
                {cfg(block, 'title', 'Agenda una llamada')}
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base" data-id="element-570" data-mp="24lmptxxvdic0">
                {cfg(block, 'text', '')}
              </p>
              <div className="mt-9 flex justify-center" data-id="element-571" data-mp="2eb6fbmg38afu">
                <Button size="lg" className="h-14 w-full rounded-xl px-8 sm:w-auto" onClick={() => {
                  if (bookingUrl) window.open(bookingUrl, '_blank', 'noopener');
                }} data-id="element-572" data-mp="2ecpvze5noni5">
                  {cfg(block, 'ctaLabel', 'Reservar hora')}
                </Button>
              </div>
              {!bookingUrl && <p className="mt-5 text-xs text-muted-foreground" data-id="element-573" data-mp="19bghdsio2gt4">
                  Conecta Google Calendar en Integraciones o añade una URL de
                  reserva a este bloque.
                </p>}
            </div>
          </section>;
        }
      case 'whatsapp':
        return <a key={block.id} href={`https://wa.me/${String(cfg(block, 'number', '')).replace(/\D/g, '')}?text=${encodeURIComponent(cfg(block, 'message', ''))}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-105" aria-label="Contactar por WhatsApp" data-id="element-574" data-mp="vc8eopnaoqk6">
            <MessageCircleIcon className="h-6 w-6" data-id="element-575" data-mp="bo7y84651jua" />
          </a>;
      case 'cta_final':
        {
          // Con número de WhatsApp el botón abre el chat; si no, baja al formulario.
          const waNumber = String(cfg(block, 'whatsappNumber', '')).replace(/\D/g, '');
          return <section key={block.id} className={`px-5 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-576" data-mp="wfmqnue214d5">
            <div className="mx-auto max-w-3xl text-center" data-id="element-577" data-mp="11e7r9ux7xq24">
              <h2 className="text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-578" data-mp="nk70bncid7cb">
                {cfg(block, 'title', '')}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground" data-id="element-579" data-mp="dmsmlvhyywnp">
                {cfg(block, 'text', '')}
              </p>
              <div className="mx-auto mt-10 flex max-w-[380px] justify-center" data-id="element-580" data-mp="im5it6uqdg2y">
                <BlockCta block={block} label={cfg(block, 'ctaLabel', 'Quiero empezar')} onClick={() => {
                  if (waNumber) {
                    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(cfg(block, 'whatsappMessage', ''))}`, '_blank', 'noopener');
                    return;
                  }
                  scrollToForm();
                }} data-id="element-581" data-mp="4mdwmiu28tdv" />
              </div>
            </div>
          </section>;
        }
      case 'footer':
        {
          // Solo se muestran las redes con URL configurada.
          const socials = cfg<{
            network: string;
            url: string;
          }[]>(block, 'socials', []).filter(s => s.url?.trim());
          return <footer key={block.id} className="border-t border-border px-5 py-12 sm:px-8" data-id="element-582" data-mp="3sj4a2ohsa1wv">
            <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-583" data-mp="b2ct21c8311z">
              <div className="flex flex-col items-center gap-4 sm:items-start" data-id="element-584" data-mp="642x3oiobwfb">
                {brandMark}
              </div>
              <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground" data-id="element-585" data-mp="k2j1h8t2daua">
                <p data-id="element-586" data-mp="f4r6g5x4h8lv">{cfg(block, 'text', '')}</p>
                <div className="flex flex-wrap justify-center gap-x-5 gap-y-1" data-id="element-587" data-mp="2ltw4fsbh18np">
                  {cfg<string[]>(block, 'links', []).map(link => <span key={link} className="cursor-pointer hover:text-foreground" data-id="element-588" data-mp="2gwmg6fztj31g">
                      {link}
                    </span>)}
                </div>
              </div>
              {socials.length > 0 && <div className="flex gap-3 text-muted-foreground" data-id="element-589" data-mp="prt0xhqh79ct">
                  {socials.map((social, index) => {
                  const network = SOCIAL_NETWORKS[social.network];
                  const Icon = network?.icon ?? LinkIcon;
                  return <a key={`${social.network}-${index}`} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={network?.label ?? social.network} className="flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-primary/40 hover:text-primary" data-id="element-590" data-mp="13rbv16mgck64" data-mpi={index}>
                        <Icon className="h-4 w-4" aria-hidden="true" data-id="element-591" data-mp="1ig81iz8v1p4m" />
                      </a>;
                })}
                </div>}
            </div>
          </footer>;
        }
      case 'servicios':
        {
          const items = cfg<{
            icon: string;
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-592" data-mp="3bwb2q3rgp0xe">
            <div className="mx-auto max-w-6xl" data-id="element-593" data-mp="371p0aq3cyipg">
              {cfg(block, 'title', '') && <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-594" data-mp="32yiutko2kvk1">
                  {cfg(block, 'title', '')}
                </h2>}
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-595" data-mp="3uone1g03fcjo">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className={`grid gap-6 md:grid-cols-3 ${cfg(block, 'title', '') || cfg(block, 'subtitle', '') ? 'mt-14' : ''}`} data-id="element-596" data-mp="3lw1wg3877bwf">
                {items.map((item, index) => {
                  const Icon = ICONS[item.icon] ?? BriefcaseIcon;
                  return <article key={`${item.title}-${index}`} className={`rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${cardShadow}`} data-id="element-597" data-mp="i9uvbobf0iev" data-mpi={index}>
                      <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10" data-id="element-598" data-mp="3mpkyrzbjsow6">
                        <Icon className="h-5 w-5 text-primary" data-id="element-599" data-mp="380yls49e8984" />
                      </span>
                      <h3 className="mb-2 text-base font-semibold text-navy" data-id="element-600" data-mp="3czi9a93mxt2l">
                        {item.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground" data-id="element-601" data-mp="4l9ldec25qgr">
                        {item.text}
                      </p>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'caracteristicas':
        {
          const items = cfg<{
            title: string;
            text: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-602" data-mp="2g87odqfz4eha">
            <div className="mx-auto max-w-4xl" data-id="element-603" data-mp="2k8zwuhh0ufci">
              <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-604" data-mp="2cqs91mn5qljf">
                {cfg(block, 'title', 'Características')}
              </h2>
              <ul className="grid gap-x-10 gap-y-6 sm:grid-cols-2" data-id="element-605" data-mp="2mqartzrc019w">
                {items.map((item, index) => <li key={`${item.title}-${index}`} className="flex gap-3" data-id="element-606" data-mp="2hqxwsrqb3nw8" data-mpi={index}>
                    <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary/10" data-id="element-607" data-mp="2hotpdzt786r7">
                      <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-608" data-mp="1p2irwy0w2lym" />
                    </span>
                    <span data-id="element-609" data-mp="23rez6sv4fuy6">
                      <span className="block text-[15px] font-medium text-foreground" data-id="element-610" data-mp="23ror4lzq8c6i">
                        {item.title}
                      </span>
                      <span className="block text-sm leading-relaxed text-muted-foreground" data-id="element-611" data-mp="1bam436696y81">
                        {item.text}
                      </span>
                    </span>
                  </li>)}
              </ul>
            </div>
          </section>;
        }
      case 'galeria':
        {
          const items = cfg<string[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-612" data-mp="2l65b3w77xyl">
            <div className="mx-auto max-w-6xl" data-id="element-613" data-mp="gkp0kw3wveth">
              {cfg(block, 'title', '') && <h2 className="mb-12 text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-614" data-mp="bn5mpxb4l422">
                  {cfg(block, 'title', '')}
                </h2>}
              {items.length === 0 ? <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-surface py-16 text-sm text-muted-foreground" data-id="element-615" data-mp="3f3vw8m5x1z8i">
                  <ImageIcon className="h-6 w-6" aria-hidden="true" data-id="element-616" data-mp="3p10max2vueul" />
                  Añade imágenes desde el editor
                </div> : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-617" data-mp="31ao48tdxb1qa">
                  {items.map((url, index) => <img key={`${url}-${index}`} src={url} alt="" loading="lazy" decoding="async" className={`aspect-[4/3] w-full rounded-2xl object-cover ${cardShadow}`} data-id="element-618" data-mp="2wd4plsdbcmxt" data-mpi={index} />)}
                </div>}
            </div>
          </section>;
        }
      case 'precios':
        {
          const items = cfg<{
            name: string;
            price: string;
            period: string;
            features: string;
            featured: string;
            ctaLabel: string;
          }[]>(block, 'items', []);
          return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]} ${surfaceClass}`} data-id="element-619" data-mp="1um3iogsz3zre">
            <div className="mx-auto max-w-5xl" data-id="element-620" data-mp="1zilgsxtsszur">
              <h2 className="text-center text-3xl font-semibold tracking-tight text-navy sm:text-4xl" data-id="element-621" data-mp="1lk3xdcaxe9in">
                {cfg(block, 'title', 'Planes')}
              </h2>
              {cfg(block, 'subtitle', '') && <p className="mx-auto mt-4 max-w-2xl text-center text-base text-muted-foreground" data-id="element-622" data-mp="a58aj324l1r0">
                  {cfg(block, 'subtitle', '')}
                </p>}
              <div className="mt-14 grid gap-6 md:grid-cols-2" data-id="element-623" data-mp="f5cuj328selz">
                {items.map((plan, index) => {
                  const featured = Boolean(plan.featured);
                  return <article key={`${plan.name}-${index}`} className={`flex flex-col rounded-2xl border bg-background p-8 ${featured ? 'border-primary shadow-soft' : 'border-border'}`} data-id="element-624" data-mp="2bvfh5z5hcs9i" data-mpi={index}>
                      {featured && <span className="mb-5 inline-flex w-fit rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary" data-id="element-625" data-mp="231129xov3jqx">
                          Recomendado
                        </span>}
                      <h3 className="text-base font-semibold text-navy" data-id="element-626" data-mp="1t21vylrkk073">{plan.name}</h3>
                      <p className="mt-4 flex items-baseline gap-2" data-id="element-627" data-mp="1o5bgo0nl98mq">
                        <span className="text-4xl font-semibold tracking-tight text-navy" data-id="element-628" data-mp="1k3ge6stx8p7d">
                          {plan.price}
                        </span>
                        <span className="text-sm text-muted-foreground" data-id="element-629" data-mp="3iggo7g1sq57c">
                          {plan.period}
                        </span>
                      </p>
                      <ul className="mt-7 flex-1 space-y-3" data-id="element-630" data-mp="108f5ksng8xsk">
                        {String(plan.features ?? '').split('\n').filter(Boolean).map(feature => <li key={feature} className="flex gap-2.5 text-sm text-muted-foreground" data-id="element-631" data-mp="155yhhlwocyhc">
                              <CheckIcon className="mt-0.5 h-4 w-4 flex-none text-primary" data-id="element-632" data-mp="1a1dw4g2iayet" />
                              {feature}
                            </li>)}
                      </ul>
                      <Button className="mt-8 w-full" variant={featured ? 'primary' : 'secondary'} onClick={scrollToForm} data-id="element-633" data-mp="hcfcfeiqjo7e">
                        {plan.ctaLabel || 'Empezar'}
                      </Button>
                    </article>;
                })}
              </div>
            </div>
          </section>;
        }
      case 'contacto':
        return <section key={block.id} className={`px-5 sm:px-8 ${SPACING[block.spacing]}`} data-id="element-634" data-mp="21qz237ztrf6r">
            <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background p-10 text-center shadow-soft" data-id="element-635" data-mp="26or5ay5x6nq9">
              <h2 className="text-2xl font-semibold tracking-tight text-navy sm:text-3xl" data-id="element-636" data-mp="258wkar9t6kli">
                {cfg(block, 'title', 'Contacto')}
              </h2>
              <p className="mt-3 text-sm text-muted-foreground" data-id="element-637" data-mp="2e8jnnim3m0h7">
                {cfg(block, 'text', '')}
              </p>
              <dl className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-638" data-mp="2a6ycq2t4jw9n">
                {[{
                icon: AtSignIcon,
                value: cfg(block, 'email', '')
              }, {
                icon: PhoneIcon,
                value: cfg(block, 'phone', '')
              }, {
                icon: MapPinIcon,
                value: cfg(block, 'address', '')
              }].filter(entry => entry.value).map(entry => <div key={entry.value} className="flex flex-col items-center gap-2 rounded-xl bg-surface px-4 py-5" data-id="element-639" data-mp="2syqxxb1ycd6t">
                      <entry.icon className="h-5 w-5 text-primary" aria-hidden="true" data-id="element-640" data-mp="36y0fae1w9dcd" />
                      <span className="break-words text-sm text-foreground" data-id="element-641" data-mp="31zozlmox48m8">
                        {entry.value}
                      </span>
                    </div>)}
              </dl>
            </div>
          </section>;
      default:
        return null;
    }
  }

  /** En el constructor cada bloque es seleccionable haciendo clic sobre él. */
  function renderEditable(block: LandingBlock) {
    const content = renderBlock(block);
    const mobile = mobileStyle(block);
    const style = {
      ...wrapperStyle(block),
      ...mobile.vars
    };
    const hasStyle = Object.values(style).some(v => v !== undefined);
    if (!onSelectBlock) {
      return hasStyle || mobile.className ? <div key={block.id} style={style} className={mobile.className} data-id="element-642" data-mp="2swjpdfu5ddcl">
          {content}
        </div> : content;
    }
    const selected = selectedBlockId === block.id;
    return <div key={block.id} role="button" tabIndex={0} onClick={() => onSelectBlock(block.id)} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectBlock(block.id);
      }
    }} data-block-id={block.id} style={style} className={`relative cursor-pointer outline-none transition-shadow ${mobile.className} ${selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : 'hover:ring-2 hover:ring-primary/30'}`} data-id="element-643" data-mp="2juaeupyvpjz7">
        {selected && <span className="pointer-events-none absolute left-3 top-3 z-40 rounded-full bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground" data-id="element-644" data-mp="gmjtm5m1vtez">
            {blockLabel(block.type)}
          </span>}
        {content}
      </div>;
  }
  return <div ref={rootRef} className={`w-full bg-background ${viewport === 'mobile' ? 'ava-viewport-mobile' : viewport === 'tablet' ? 'ava-viewport-tablet' : ''}`} style={{
    '--primary': config.primaryColor,
    fontFamily: `${config.fontFamily}, Inter, sans-serif`,
    // Fondo apenas perceptible hacia azul muy claro, sin perder legibilidad.
    ...(theme === 'blanco_premium' ? {
      background: 'linear-gradient(180deg,#FFFFFF 0%,#F8FAFD 45%,#F4F7FD 100%)'
    } : {})
  } as React.CSSProperties} data-id="element-645" data-mp="18dzd3qsb900z">
      {blocks.map(renderEditable)}
      {blocks.length === 0 && <div className="flex min-h-[300px] items-center justify-center p-10 text-center text-sm text-muted-foreground" data-id="element-646" data-mp="24j9i9lcdaf9k">
          Esta landing no tiene bloques visibles. Activa alguno desde el editor.
        </div>}
    </div>;
}